import {
  Divider,
  makeStyles,
  mergeClasses,
  SearchBoxChangeEvent,
  Switch,
} from "@fluentui/react-components";
import React, { useCallback, useContext, useId, useState } from "react";
import Select from "react-select";
import { LayoutContext } from "../../../context/LayoutContext";
import { $cloud50, $sky60 } from "../../../cssjs/colors";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { TIMETYPE_OPTIONS } from "../../../utils/common/Constants";
import { findMatches } from "../../../utils/multi-airports/MultiAirportHelperFunctions";
import GlobalDateTimeFilter from "../../filters/global-filter/GlobalDateTimeFilter";
import GlobalFilter from "../../filters/global-filter/GlobalFilter";
import AppNotificationBanner from "../../notifications/AppNotificationBanner";
import MapDisplayFlights from "../../osm-maps/map-displays/MapDisplayFlights";
import { OverviewSidebar } from "../../overview-sidebar/OverviewSidebar";
import Breadcrumb from "../breadcrumb/CustomBreadcrumb";

export type Timezone = "LT" | "UTC";

const Layout: React.FunctionComponent<{ children?: React.ReactElement }> = ({
  children,
}) => {
  const selectId = useId();
  const classes = useStyles();

  const {
    isMapView,
    setIsMapView,
    isMappedInView,
    setIsMappedInView,
    timezone,
    currentTime,
    globalFilterDate,
    globalStaticFilterDate,
    setCurrentTime,
    setSelectedFilter,
    setTimezone,
    setSelectedFlight,
    setGlobalFilterDate,
    setGlobalStaticFilterDate,
  } = useContext(LayoutContext);

  const [sideBarActive, setSideBarActive] = useState<boolean>(true);

  const airportCode = useAppSelector((state) => {
    const stored = sessionStorage.getItem("airportCode");
    if (stored) {
      return stored;
    }
    return getAirportCode(state);
  });

  const airportConfig = getAirportConfig(airportCode);
  //Temporary code to handle airportCode specific changes
  const isYQBAirport = airportCode === "YQB";

  const urlPath = location?.pathname;
  const isOverview = urlPath?.startsWith("/home");
  const isMultiAirport = urlPath?.startsWith("/multi-airports");
  const isFlight = urlPath?.startsWith("/flights");
  const isBaggage = urlPath?.startsWith("/baggage");
  const isPassenger = urlPath?.startsWith("/pax");
  const isResourceAllocation = urlPath?.startsWith("/resource-allocation");
  const isSingleFlightView = urlPath?.startsWith("/single-flight-view");

  const isTeamsChannelPage =
    urlPath?.startsWith("/event-tasks") ||
    urlPath?.startsWith("/crisis-dashboard");

  const isTabletPortrait = useMediaQuery("(max-width: 1024px)");
  const isTabletLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1370px)"
  );
  const minHeight = isTabletLandscape
    ? "max(calc(100dvh - 196px + var(--amd-ms-teams-height-offset)), 500px)"
    : "max(calc(100dvh - 188px + var(--amd-ms-teams-height-offset)), 500px)";
  const contentMinHeight = isMapView || isMappedInView ? minHeight : undefined;

  const showGlobalDateTimeFilter =
    isOverview || isFlight || isBaggage || isPassenger || isResourceAllocation;
  const shouldDisplayMapView = isMapView;

  function searchInputHandler(event: SearchBoxChangeEvent): void {
    const { target } = event;
    const { value } = target as HTMLInputElement;
    const matchedAirports = findMatches(value, []);
    console.log("matchedAirports", matchedAirports);
  }

  const handleMapView = useCallback(
    (ev: {
      currentTarget: { checked: boolean | ((prevState: boolean) => boolean) };
    }) => {
      const isChecked = ev?.currentTarget?.checked ?? false;
      sessionStorage.setItem("isMapView", String(isChecked));
      setIsMapView(isChecked);

      if (isChecked && isMappedInView) {
        sessionStorage.setItem("isMappedInView", "false");
        setIsMappedInView(false);
      }
    },
    [isMappedInView, setIsMapView, setIsMappedInView]
  );

  const handleMappedInView = useCallback(
    (ev: {
      currentTarget: { checked: boolean | ((prevState: boolean) => boolean) };
    }) => {
      const isChecked = ev?.currentTarget?.checked ?? false;
      setIsMappedInView(isChecked);
      sessionStorage.setItem("isMappedInView", String(isChecked));

      if (isChecked && isMapView) {
        setIsMapView(false);
        sessionStorage.setItem("isMapView", "false");
      }
    },
    [isMapView, setIsMappedInView, setIsMapView]
  );

  //Teams channel page should not have layout container
  if (isTeamsChannelPage) {
    return <>{children}</>;
  }

  return (
    <section data-testid="layoutContainer" className={classes.layoutContainer}>
      <div
        className={mergeClasses(
          "amd-container-vcenter",
          "amd-container-hspacebtwn mb-2",
          isSingleFlightView && classes.globalFilter
        )}
      >
        {!isSingleFlightView && (
          <Breadcrumb
            handleMapView={handleMapView}
            airportCode={airportCode}
            setSelectedFlight={setSelectedFlight}
          />
        )}
        <div className="amd-container-center">
          <Switch
            checked={isMapView}
            label={`Map view`}
            labelPosition="after"
            onChange={handleMapView}
          />
          <Divider
            className={mergeClasses(classes.divider, "px-sm-1")}
            vertical
            appearance="strong"
          />
          {!isYQBAirport &&
            process.env.REACT_APP_SHOW_MAPPED_IN_VIEW === "true" &&
            isFlight && (
              <>
                <Switch
                  checked={isMappedInView}
                  label="Terminal view"
                  labelPosition="after"
                  onChange={handleMappedInView}
                />
                <Divider
                  className={mergeClasses(classes.divider, "px-sm-1")}
                  vertical
                  appearance="strong"
                />
              </>
            )}

          <>
            {!isMultiAirport && (
              <>
                <GlobalDateTimeFilter
                  airportCode={airportCode}
                  timezone={timezone}
                  globalFilterDate={globalFilterDate}
                  globalStaticFilterDate={globalStaticFilterDate}
                  setGlobalFilterDate={setGlobalFilterDate}
                  showGlobalDateTimeFilter={showGlobalDateTimeFilter}
                  currentTime={currentTime}
                  setCurrentTime={setCurrentTime}
                  setSelectedFilter={setSelectedFilter}
                  setGlobalStaticFilterDate={setGlobalStaticFilterDate}
                />

                <Divider
                  className={mergeClasses(
                    classes.divider,
                    "pr-sm-3",
                    "px-sm-1"
                  )}
                  vertical
                  appearance="strong"
                />

                <Select
                  id={selectId}
                  aria-label="Time zone selection"
                  options={TIMETYPE_OPTIONS}
                  value={TIMETYPE_OPTIONS.find(
                    (option) => option.value === timezone
                  )}
                  onChange={(selectedOption: any) => {
                    setTimezone(selectedOption.value);
                  }}
                  styles={customStyles}
                  isSearchable={false}
                  menuPortalTarget={document.body} // important
                />
              </>
            )}
          </>
          <GlobalFilter
            className="d-none"
            label={isTabletPortrait ? "" : "Global"}
          />
        </div>
      </div>

      <AppNotificationBanner />

      <section
        className={mergeClasses(
          isMapView || isMappedInView
            ? classes.mapViewContainer
            : classes.defaultContainer
        )}
        style={{ minHeight: contentMinHeight }}
        data-testid="layout-content"
      >
        {!isMultiAirport && shouldDisplayMapView ? (
          <>
            <OverviewSidebar
              sideBarActive={sideBarActive}
              setSideBarActive={setSideBarActive}
              searchInputHandler={searchInputHandler}
              airportCode={airportCode}
            />
            <MapDisplayFlights
              airportMapDetail={{
                id: airportConfig.iataCode,
                code: airportConfig.iataCode,
                name: airportConfig.name,
                location: airportConfig.location as [number, number],
              }}
              setSideBarActive={setSideBarActive}
            />
          </>
        ) : (
          children
        )}
      </section>
    </section>
  );
};

export default Layout;

const useStyles = makeStyles({
  layoutContainer: {
    boxSizing: "border-box",
    paddingBottom: "4px",
  },
  airportHeading: {
    color: "#000835",
    "&:hover": {
      color: `${$sky60} !important`,
    },
  },
  breadcrumbsIcon: {
    flexDirection: "row-reverse",
    display: "flex",
  },
  iconButton: {
    border: `1px solid ${$sky60}`,
    padding: "5px",
    margin: "0px",
    color: $sky60,
  },
  defaultContainer: {
    paddingTop: "8px",
  },
  mapViewContainer: {
    boxSizing: "border-box",
    background: "#F8F8F8",
    paddingTop: "8px",
    borderRadius: "8px",
    border: `1px solid ${$cloud50}`,
    position: "relative",
  },
  breadcrumb: {
    flexDirection: "row-reverse",
    display: "flex",
    "& .ms-Dropdown-title": {
      background: "none",
      border: "none",
    },
    "&.is-active .ms-Dropdown-title": {
      border: "none",
    },
  },
  globalFilter: {
    justifyContent: "end",
  },
  divider: {
    minHeight: "32px",
  },
});

const customStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "10px",
    minWidth: "85px",
    textAlign: "center",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "500",
  }),

  menuPortal: (base: any) => ({
    ...base,
    zIndex: 1000,
  }),
};
